/**
 * orthogonalRoute.ts
 *
 * Pure, dependency-free orthogonal (axis-aligned) routing helper used by the
 * opt-in "smart" edge type. Given a source point, a target point, and a list of
 * axis-aligned obstacle rectangles, it produces a polyline of orthogonal
 * segments that attempts to avoid passing through the interior of any obstacle.
 *
 * Design goals:
 *  - Pure function (no React / DOM), so it is trivially unit-testable.
 *  - Behaviour-preserving for the rest of the app: nothing here touches the
 *    default "custom" edge. This is only consumed by SmartEdge.tsx.
 *  - Pragmatic heuristic rather than a full pathfinder: we try a small set of
 *    candidate L / Z shaped orthogonal routes and pick the first that is clear
 *    of obstacle interiors. If none is clear we fall back to routing around the
 *    union bounding box of the blocking obstacles.
 *
 * The contract verified by the unit test: the returned polyline never passes
 * through the strict interior of any obstacle rectangle for simple cases.
 */

export interface Point {
  x: number
  y: number
}

export interface Rect {
  x: number
  y: number
  width: number
  height: number
}

export interface OrthogonalRouteOptions {
  /**
   * Padding applied around every obstacle so the route keeps a visible gap and
   * does not hug node borders. Defaults to a small value.
   */
  padding?: number
}

const DEFAULT_PADDING = 12
const EPSILON = 0.0001

function inflate(rect: Rect, by: number): Rect {
  return {
    x: rect.x - by,
    y: rect.y - by,
    width: rect.width + by * 2,
    height: rect.height + by * 2,
  }
}

/** True only when (x, y) is strictly inside the rectangle interior. */
function pointStrictlyInside(rect: Rect, x: number, y: number): boolean {
  return (
    x > rect.x + EPSILON &&
    x < rect.x + rect.width - EPSILON &&
    y > rect.y + EPSILON &&
    y < rect.y + rect.height - EPSILON
  )
}

/**
 * Does an axis-aligned segment pass through the strict interior of a rect?
 * Segments are either horizontal or vertical (orthogonal routing only).
 */
function segmentCrossesRectInterior(a: Point, b: Point, rect: Rect): boolean {
  const left = rect.x
  const right = rect.x + rect.width
  const top = rect.y
  const bottom = rect.y + rect.height

  // Degenerate (zero-length) rect can never be "entered".
  if (right - left <= EPSILON || bottom - top <= EPSILON) return false

  if (Math.abs(a.y - b.y) < EPSILON) {
    // Horizontal segment at y = a.y spanning [minX, maxX].
    const y = a.y
    if (y <= top + EPSILON || y >= bottom - EPSILON) return false
    const minX = Math.min(a.x, b.x)
    const maxX = Math.max(a.x, b.x)
    // Overlap of [minX, maxX] with the open interval (left, right).
    return maxX > left + EPSILON && minX < right - EPSILON
  }

  if (Math.abs(a.x - b.x) < EPSILON) {
    // Vertical segment at x = a.x spanning [minY, maxY].
    const x = a.x
    if (x <= left + EPSILON || x >= right - EPSILON) return false
    const minY = Math.min(a.y, b.y)
    const maxY = Math.max(a.y, b.y)
    return maxY > top + EPSILON && minY < bottom - EPSILON
  }

  // Non-orthogonal segments are not expected here; treat conservatively by
  // sampling endpoints only (callers always pass orthogonal segments).
  return pointStrictlyInside(rect, a.x, a.y) || pointStrictlyInside(rect, b.x, b.y)
}

/** True when the whole polyline avoids every obstacle interior. */
function polylineIsClear(points: Point[], obstacles: Rect[]): boolean {
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]
    const b = points[i + 1]
    for (const obstacle of obstacles) {
      if (segmentCrossesRectInterior(a, b, obstacle)) {
        return false
      }
    }
  }
  return true
}

/** Remove consecutive duplicate / collinear points to keep the polyline tidy. */
function simplify(points: Point[]): Point[] {
  const out: Point[] = []
  for (const p of points) {
    const last = out[out.length - 1]
    if (last && Math.abs(last.x - p.x) < EPSILON && Math.abs(last.y - p.y) < EPSILON) {
      continue
    }
    out.push(p)
  }
  // Drop the middle of any three collinear points.
  const collapsed: Point[] = []
  for (let i = 0; i < out.length; i++) {
    const prev = collapsed[collapsed.length - 1]
    const cur = out[i]
    const next = out[i + 1]
    if (prev && next) {
      const collinearH =
        Math.abs(prev.y - cur.y) < EPSILON && Math.abs(cur.y - next.y) < EPSILON
      const collinearV =
        Math.abs(prev.x - cur.x) < EPSILON && Math.abs(cur.x - next.x) < EPSILON
      if (collinearH || collinearV) {
        continue
      }
    }
    collapsed.push(cur)
  }
  return collapsed
}

/** Candidate orthogonal routes between two points (L-shapes and a Z-shape). */
function candidateRoutes(source: Point, target: Point): Point[][] {
  const midX = (source.x + target.x) / 2
  const midY = (source.y + target.y) / 2
  return [
    // Horizontal-first L
    [source, { x: target.x, y: source.y }, target],
    // Vertical-first L
    [source, { x: source.x, y: target.y }, target],
    // Z through vertical mid line
    [
      source,
      { x: midX, y: source.y },
      { x: midX, y: target.y },
      target,
    ],
    // Z through horizontal mid line
    [
      source,
      { x: source.x, y: midY },
      { x: target.x, y: midY },
      target,
    ],
  ]
}

/** Union bounding box of a set of rects. */
function unionRect(rects: Rect[]): Rect {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const r of rects) {
    minX = Math.min(minX, r.x)
    minY = Math.min(minY, r.y)
    maxX = Math.max(maxX, r.x + r.width)
    maxY = Math.max(maxY, r.y + r.height)
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
}

/**
 * Build "go around" routes that detour past one side of a blocking box. Each
 * route leaves the source, travels to a point clear of the box on one of its
 * four sides, then approaches the target orthogonally.
 */
function detourRoutes(source: Point, target: Point, box: Rect): Point[][] {
  const left = box.x
  const right = box.x + box.width
  const top = box.y
  const bottom = box.y + box.height

  const routes: Point[][] = []

  // Detour over the top.
  routes.push([
    source,
    { x: source.x, y: top },
    { x: target.x, y: top },
    target,
  ])
  // Detour under the bottom.
  routes.push([
    source,
    { x: source.x, y: bottom },
    { x: target.x, y: bottom },
    target,
  ])
  // Detour to the left.
  routes.push([
    source,
    { x: left, y: source.y },
    { x: left, y: target.y },
    target,
  ])
  // Detour to the right.
  routes.push([
    source,
    { x: right, y: source.y },
    { x: right, y: target.y },
    target,
  ])

  return routes
}

function routeLength(points: Point[]): number {
  let total = 0
  for (let i = 0; i < points.length - 1; i++) {
    total += Math.abs(points[i + 1].x - points[i].x) + Math.abs(points[i + 1].y - points[i].y)
  }
  return total
}

/**
 * Compute an orthogonal polyline from `source` to `target` that avoids the
 * interiors of `obstacles`.
 *
 * Returns at least two points (source and target). The first point is always
 * exactly `source` and the last is always exactly `target`, so the rendered
 * edge still connects the intended handles.
 */
export function computeOrthogonalRoute(
  source: Point,
  target: Point,
  obstacles: Rect[] = [],
  options: OrthogonalRouteOptions = {}
): Point[] {
  const padding = options.padding ?? DEFAULT_PADDING

  // Inflate obstacles for clearance, then drop any that contain an endpoint
  // (the edge legitimately starts/ends on a node border).
  const inflated = obstacles
    .map((rect) => inflate(rect, padding))
    .filter(
      (rect) =>
        !pointStrictlyInside(rect, source.x, source.y) &&
        !pointStrictlyInside(rect, target.x, target.y)
    )

  // No obstacles in the way: prefer the simplest clear L/Z route.
  for (const route of candidateRoutes(source, target)) {
    if (polylineIsClear(route, inflated)) {
      return simplify(route)
    }
  }

  // Identify the obstacles actually blocking the simplest candidate so we can
  // detour around their union.
  const blocking = inflated.filter((rect) =>
    candidateRoutes(source, target).every((route) => {
      for (let i = 0; i < route.length - 1; i++) {
        if (segmentCrossesRectInterior(route[i], route[i + 1], rect)) {
          return true
        }
      }
      return false
    })
  )

  const blockers = blocking.length > 0 ? blocking : inflated
  const box = unionRect(blockers)

  // Try detours around the union box; keep the shortest clear one.
  let best: Point[] | null = null
  let bestLen = Infinity
  for (const route of detourRoutes(source, target, box)) {
    if (polylineIsClear(route, inflated)) {
      const len = routeLength(route)
      if (len < bestLen) {
        bestLen = len
        best = route
      }
    }
  }

  if (best) {
    return simplify(best)
  }

  // Last resort: detour fully around the outside of the union box. This always
  // clears the box itself even when individual detours are partially blocked.
  const margin = padding
  const left = box.x - margin
  const right = box.x + box.width + margin
  const top = box.y - margin
  const bottom = box.y + box.height + margin

  // Route along the nearest outer edge: go to the top-outside, across, then down.
  const around: Point[] = [
    source,
    { x: source.x, y: top },
    { x: left, y: top },
    { x: left, y: bottom },
    { x: right, y: bottom },
    { x: right, y: top },
    { x: target.x, y: top },
    target,
  ]
  if (polylineIsClear(around, inflated)) {
    return simplify(around)
  }

  // Truly degenerate fallback: a vertical-first L. Better a visible edge than
  // none; this matches React Flow's own fallback behaviour.
  return simplify([source, { x: source.x, y: target.y }, target])
}

/**
 * Convert a polyline into an SVG path string. Exposed so the edge component can
 * render the computed route directly with BaseEdge.
 */
export function polylineToSvgPath(points: Point[]): string {
  if (points.length === 0) return ''
  const [first, ...rest] = points
  let d = `M ${first.x},${first.y}`
  for (const p of rest) {
    d += ` L ${p.x},${p.y}`
  }
  return d
}

/** Midpoint of the polyline by arc length, used for label placement. */
export function polylineMidpoint(points: Point[]): Point {
  if (points.length === 0) return { x: 0, y: 0 }
  if (points.length === 1) return points[0]
  const total = routeLength(points)
  const half = total / 2
  let acc = 0
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]
    const b = points[i + 1]
    const segLen = Math.abs(b.x - a.x) + Math.abs(b.y - a.y)
    if (acc + segLen >= half) {
      const remaining = half - acc
      const ratio = segLen === 0 ? 0 : remaining / segLen
      return {
        x: a.x + (b.x - a.x) * ratio,
        y: a.y + (b.y - a.y) * ratio,
      }
    }
    acc += segLen
  }
  return points[points.length - 1]
}
